/**
 * Session Query Hooks
 *
 * TanStack Query hooks for dev session data, all routed through the API server.
 */

import type { DevSessionStatus } from '@cortex/shared';
import { useQuery } from '@tanstack/react-query';

import { useFetchWithAuth } from './use-api';

interface DevSession {
  id: string;
  requestId: string;
  status: DevSessionStatus;
  machineId: string | null;
  flyHostname: string | null;
  previewUrl: string | null;
  websocketUrl: string | null;
  createdAt: string;
  lastActiveAt: string;
  suspendedAt: string | null;
  provisioningStage: string | null;
  provisioningError: string | null;
}

export function useSessionByRequest(requestId: string | undefined) {
  const fetchWithAuth = useFetchWithAuth();

  return useQuery({
    queryKey: ['dev-session', requestId],
    queryFn: async () => {
      const response = await fetchWithAuth(`/sessions/request/${requestId}`);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error('Failed to fetch session');
      }
      const json = await response.json();
      return json.data as DevSession;
    },
    enabled: !!requestId,
    staleTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const session = query.state.data;
      if (!session) return false;
      if (session.status === 'provisioning') return 2000;
      if (session.status === 'active') return 30000;
      return false;
    },
  });
}
